/**
 * RecatalogConfirmModal — modal de confirmación para bulk re-catalog.
 * Muestra costo estimado, items count, y prompt activo.
 *
 * Spec UX: DATA_HEALTH_PANEL_SPEC.md §5.2
 *
 * Costo estimado simple: $0.002 USD por item (placeholder). El usuario aprobó
 * esta heurística en las 6 decisiones clave.
 */
import React, { useState } from 'react';
import { RefreshCw, X, Loader2, AlertTriangle } from 'lucide-react';
import type { DataHealthItem } from '../../../types/dataHealth';

interface Props {
  isOpen: boolean;
  selectedItems: DataHealthItem[];
  activePromptVersion?: string | null;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const COST_PER_ITEM_USD = 0.002;

const RecatalogConfirmModal: React.FC<Props> = ({
  isOpen,
  selectedItems,
  activePromptVersion,
  onClose,
  onConfirm,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!isOpen) return null;

  const n = selectedItems.length;
  const estCostUsd = (n * COST_PER_ITEM_USD).toFixed(3);
  const estMinutes = Math.max(1, Math.ceil(n / 60)); // ~60 items/min

  const handleConfirm = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await onConfirm();
      onClose();
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message || 'Error desconocido';
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-amber-600" />
            <h3 className="text-base font-semibold text-gray-900">
              Re-catalogar {n} producto{n === 1 ? '' : 's'}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 text-xs">
            <span className="text-gray-500">Prompt activo:</span>
            <span className="font-mono text-gray-800">{activePromptVersion || '—'}</span>
            <span className="text-gray-500">Items a procesar:</span>
            <span className="tabular-nums font-medium text-gray-800">{n.toLocaleString('es-AR')}</span>
            <span className="text-gray-500">Costo estimado:</span>
            <span className="tabular-nums font-medium text-gray-800">~ ${estCostUsd} USD</span>
            <span className="text-gray-500">Tiempo estimado:</span>
            <span className="tabular-nums font-medium text-gray-800">~ {estMinutes} min</span>
          </div>

          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border-l-2 border-amber-300 rounded px-2 py-1.5">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span>
              Esta acción <strong>sobreescribe</strong> el catalog actual (excepto campos
              en <code>manuallyEditedFields</code>). Vas a poder seguir el progreso en
              Catalogador IA → Histórico.
            </span>
          </div>

          {err && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {err}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Lanzar batch
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecatalogConfirmModal;
